import { API_BASE } from './astrology';
import authFetch from './authFetch';

export type CommissionTierId = 'emerging' | 'skilled' | 'master';
export type CommissionStatus = 'pending' | 'paid' | 'claimed' | 'in_progress' | 'delivered' | 'completed' | 'refunded';
export type ArtistStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export interface CommissionTier {
  id: CommissionTierId;
  label: string;
  stars: string;
  price: number;
  animationAddon: number;
  description: string;
  artistLevel: string;
}

export const COMMISSION_TIERS: CommissionTier[] = [
  {
    id: 'emerging',
    label: 'Emerging Artist',
    stars: '✦',
    price: 29.99,
    animationAddon: 20,
    description: 'Fresh talent with passion and dedication. Full color portrait in 1-2 hours.',
    artistLevel: 'Beginner'
  },
  {
    id: 'skilled',
    label: 'Skilled Artist',
    stars: '✦✦',
    price: 59.99,
    animationAddon: 20,
    description: 'Experienced artist with refined technique. Full color portrait in 1-2 hours.',
    artistLevel: 'Intermediate'
  },
  {
    id: 'master',
    label: 'Master Artist',
    stars: '✦✦✦',
    price: 99.99,
    animationAddon: 20,
    description: 'Professional-grade artistry. Stunning full color portrait in 1-2 hours.',
    artistLevel: 'Professional'
  }
];

export interface Commission {
  commissionId: string;
  userId: string;
  tier: CommissionTierId;
  withAnimation: boolean;
  totalPrice: number;
  status: CommissionStatus;
  userNotes?: string;
  artistId?: string;
  artistName?: string;
  artworkUrl?: string;
  createdAt: string;
  claimedAt?: string;
  deliveredAt?: string;
  archetype?: string;
  subArchetype?: string;
  attributes?: Record<string, number>;
}

export interface Artist {
  artistId: string;
  email: string;
  name: string;
  portfolioUrl?: string;
  tier: CommissionTierId;
  status: ArtistStatus;
  bio?: string;
  sampleImages?: string[];
  totalEarned: number;
  commissionsCompleted: number;
  createdAt: string;
  approvedAt?: string;
}

export interface ArtistApplication {
  applicationId: string;
  email: string;
  name: string;
  portfolioUrl: string;
  preferredTier: CommissionTierId;
  bio: string;
  sampleImages?: string[];
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

// User-facing commission endpoints
export async function createCommission(
  userId: string,
  tier: CommissionTierId,
  withAnimation: boolean,
  userNotes?: string
): Promise<{ checkoutUrl: string; commissionId: string }> {
  const res = await fetch(`${API_BASE}/commission/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, tier, withAnimation, userNotes })
  });
  if (!res.ok) throw new Error('Failed to create commission');
  return res.json();
}

export async function getMyCommissions(userId: string): Promise<Commission[]> {
  const res = await fetch(`${API_BASE}/commission/my-commissions?userId=${userId}`);
  if (!res.ok) throw new Error('Failed to fetch commissions');
  return res.json();
}

export async function getCommissionById(userId: string, commissionId: string): Promise<Commission> {
  const res = await fetch(`${API_BASE}/commission/${commissionId}?userId=${userId}`);
  if (!res.ok) throw new Error('Failed to fetch commission');
  return res.json();
}

export async function setCommissionAsAvatar(userId: string, commissionId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/commission/${commissionId}/set-avatar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId })
  });
  if (!res.ok) throw new Error('Failed to set avatar');
}

// Artist endpoints
export async function applyAsArtist(
  userId: string,
  application: {
    name: string;
    portfolioUrl: string;
    preferredTier: CommissionTierId;
    bio: string;
    sampleImages?: string[];
  }
): Promise<{ applicationId: string }> {
  const res = await fetch(`${API_BASE}/artist/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...application })
  });
  if (!res.ok) throw new Error('Failed to submit application');
  return res.json();
}

export async function getArtistProfile(userId: string): Promise<Artist | null> {
  const res = await fetch(`${API_BASE}/artist/profile?userId=${userId}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch artist profile');
  return res.json();
}

export async function getAvailableCommissions(userId: string): Promise<Commission[]> {
  const res = await fetch(`${API_BASE}/artist/available-commissions?userId=${userId}`);
  if (!res.ok) throw new Error('Failed to fetch available commissions');
  return res.json();
}

export async function getMyArtistCommissions(userId: string): Promise<Commission[]> {
  const res = await fetch(`${API_BASE}/artist/my-commissions?userId=${userId}`);
  if (!res.ok) throw new Error('Failed to fetch my commissions');
  return res.json();
}

export async function claimCommission(userId: string, commissionId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/artist/claim/${commissionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId })
  });
  if (!res.ok) throw new Error('Failed to claim commission');
}

export async function uploadArtwork(
  userId: string,
  commissionId: string,
  file: File
): Promise<{ artworkUrl: string }> {
  // Convert file to base64 for JSON transport (Lambda doesn't handle multipart well)
  const base64 = await fileToBase64(file);
  const res = await fetch(`${API_BASE}/artist/upload/${commissionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, imageData: base64, contentType: file.type })
  });
  if (!res.ok) throw new Error('Failed to upload artwork');
  return res.json();
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data:image/...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Admin endpoints (authenticated via JWT Bearer token)
export async function adminGetArtists(): Promise<Artist[]> {
  const res = await authFetch(`${API_BASE}/admin/artists`);
  if (!res.ok) throw new Error('Failed to fetch artists');
  return res.json();
}

export async function adminGetApplications(): Promise<ArtistApplication[]> {
  const res = await authFetch(`${API_BASE}/admin/artist-applications`);
  if (!res.ok) throw new Error('Failed to fetch applications');
  return res.json();
}

export async function adminApproveArtist(
  applicationId: string,
  tier: CommissionTierId
): Promise<void> {
  const res = await authFetch(`${API_BASE}/admin/approve-artist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ applicationId, tier })
  });
  if (!res.ok) throw new Error('Failed to approve artist');
}

export async function adminRejectArtist(
  applicationId: string
): Promise<void> {
  const res = await authFetch(`${API_BASE}/admin/reject-artist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ applicationId })
  });
  if (!res.ok) throw new Error('Failed to reject artist');
}

export async function adminUpdateArtistTier(
  artistId: string,
  tier: CommissionTierId
): Promise<void> {
  const res = await authFetch(`${API_BASE}/admin/update-artist-tier`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ artistId, tier })
  });
  if (!res.ok) throw new Error('Failed to update artist tier');
}

export async function adminGetCommissions(): Promise<Commission[]> {
  const res = await authFetch(`${API_BASE}/admin/commissions`);
  if (!res.ok) throw new Error('Failed to fetch commissions');
  return res.json();
}

export async function adminGetStats(): Promise<{
  totalUsers: number;
  activeCommissions: number;
  totalRevenue: number;
  pendingApplications: number;
}> {
  const res = await authFetch(`${API_BASE}/admin/commission-stats`);
  if (!res.ok) throw new Error('Failed to fetch stats');
  return res.json();
}
